import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, FileText, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useDebounce } from '@/hooks/useDebounce'

function HighlightedText({ text, className }: { text: string; className?: string }) {
  if (!text) return null
  const parts = text.split(/<<|>>/)
  // Нечётные индексы — подсвеченные слова (между << и >>)
  if (parts.length <= 1) return <span className={className}>{text}</span>
  return (
    <span className={className}>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="bg-yellow-300/40 text-telegram-text rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  )
}

export default function SearchPage() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)

  const { data: plan } = useQuery({
    queryKey: ['user-plan'],
    queryFn: () => apiClient.getUserPlan(),
    staleTime: 5 * 60 * 1000,
  })

  const { data: results, isLoading } = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: () => apiClient.search(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
  })

  return (
    <div className="search-page flex flex-col min-h-full">
      {/* Search bar */}
      <div className="search-header sticky top-0 z-10 glass-header px-4 pt-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-telegram-hint" size={18} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск по статьям..."
            autoFocus
            className="w-full pl-10 pr-10 py-3 glass-input text-sm text-telegram-text placeholder:text-telegram-hint"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full glass-icon active:scale-90 transition-transform"
            >
              <X size={12} className="text-telegram-hint" />
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="search-results flex-1 px-3 pb-4">
        {isLoading && debouncedQuery.length >= 2 && (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-telegram-button/30 border-t-telegram-button" />
              <span className="text-xs text-telegram-hint">Ищем...</span>
            </div>
          </div>
        )}

        {results && results.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1 mb-2">
              <p className="text-xs text-telegram-hint">
                Найдено: {results.length}
              </p>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full glass-icon ${
                plan?.features.vector_search
                  ? 'text-green-600'
                  : 'text-telegram-hint'
              }`}>
                {plan?.features.vector_search ? '🔍 Семантический' : '📝 Базовый'}
              </span>
            </div>
            {results.map((result) => (
              <button
                key={result.id}
                onClick={() => navigate(`/law/${result.law_code}/article/${result.id}`)}
                className="search-result-card glass-card w-full flex items-start gap-3 p-3 text-left"
              >
                <div className="w-8 h-8 rounded-lg glass-icon flex items-center justify-center shrink-0 mt-0.5 relative z-10">
                  <FileText size={15} className="text-telegram-hint" />
                </div>
                <div className="flex-1 min-w-0 relative z-10">
                  <div className="text-sm font-medium leading-snug line-clamp-2">
                    <HighlightedText text={result.title} />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-full glass-icon text-telegram-button">
                      {result.law_code}
                    </span>
                  </div>
                  {result.snippet && (
                    <div className="text-xs text-telegram-hint mt-1.5 line-clamp-2 leading-relaxed">
                      <HighlightedText text={result.snippet} />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {debouncedQuery.length >= 2 && !isLoading && (!results || results.length === 0) && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full glass-icon flex items-center justify-center mb-3">
              <Search size={24} className="text-telegram-hint/50" />
            </div>
            <p className="text-sm text-telegram-hint">Ничего не найдено</p>
            <p className="text-xs text-telegram-hint/70 mt-1">Попробуйте другой запрос</p>
          </div>
        )}

        {debouncedQuery.length < 2 && !isLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full glass-icon flex items-center justify-center mb-3">
              <Search size={24} className="text-telegram-button/50" />
            </div>
            <p className="text-sm text-telegram-hint">Поиск по кодексам</p>
            <p className="text-xs text-telegram-hint/70 mt-1">Введите минимум 2 символа</p>
          </div>
        )}
      </div>
    </div>
  )
}
